import type { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import type ProposalField from "work/proposals/proposal-fields/proposal-field";
import type { ProposalContentOperation } from "work/proposals/negotiation/proposal-collaboration-contract";
import { proposalTimelinePathForField } from "work/proposals/negotiation/proposal-field-timeline-path";
import { mergeProposalOperations } from "work/proposals/negotiation/proposal-operation-merger";
import {
  proposalSeenCursorForPath,
  type ProposalUnreadEnvelope,
} from "work/proposals/negotiation/proposal-field-unread";

export type ProposalCollisionKeys = {
  paths: ReadonlySet<string>;
  conflictKeys: ReadonlySet<string>;
};

export const emptyProposalCollisionKeys: ProposalCollisionKeys = {
  paths: new Set<string>(),
  conflictKeys: new Set<string>(),
};

/**
 * The paths/conflict keys where the viewer's own side needs to notice a collision with their own
 * pending work — the read-only signal behind the field-change badge's collision (error) tone. A
 * field collides when the three-way merge of the viewer's pending/in-flight operations onto the
 * envelope projection reports a conflict on the field's path (the viewer edited a value someone has
 * since moved).
 *
 * An incoming COUNTERPARTY change sitting unresolved is deliberately NOT a collision: that is what
 * redlining exists to surface and reconcile, not an alert.
 *
 * This previously also flagged an own-party REVIEWER change sitting unresolved on a field the viewer
 * had also locally edited. That branch became unreachable once reviewer resolution targets were
 * retired server-side (ROADMAP.md item 2: a reviewer's own-party edit is never a resolution target
 * for any viewer, so `resolutionStates` could never carry one to react to here). The alert itself
 * survives on the merge path above — a teammate's saved change landing under the viewer's unsaved
 * edit is exactly what the three-way merge reports.
 *
 * An alert the user cannot dismiss is nagging, so a collision the viewer has ACKNOWLEDGED — by
 * opening that field's change history, which advances their seen cursor past the change that caused
 * it — stops sounding the alarm. The conflict itself is untouched: the merge still applies at save
 * time, the field still shows its unviewed-change count, and a NEWER conflicting change (one past
 * the acknowledged cursor) raises the alert again.
 */
export function proposalCollisionKeys(
  projection: ProposalJSON,
  localOperations: readonly ProposalContentOperation[],
  acknowledgement?: ProposalCollisionAcknowledgement,
): ProposalCollisionKeys {
  const paths = new Set<string>();
  const conflictKeys = new Set<string>();

  for (const { operation } of mergeProposalOperations(projection, [...localOperations]).conflicts) {
    if (collisionIsAcknowledged(operation.path, acknowledgement)) continue;
    paths.add(operation.path);
    conflictKeys.add(operation.conflictKey);
  }

  return { paths, conflictKeys };
}

/**
 * What the viewer has already seen, and when each path last changed — enough to tell an unacknowledged
 * collision from one the viewer has already looked at.
 */
export type ProposalCollisionAcknowledgement = Pick<
  ProposalUnreadEnvelope,
  "viewerSeenThroughStreamVersion" | "viewerSeenAllThroughStreamVersion"
> & {
  changeLines?: readonly { path: string; lastChangedSequence: number }[];
};

function collisionIsAcknowledged(path: string, acknowledgement?: ProposalCollisionAcknowledgement): boolean {
  if (!acknowledgement) return false;

  const lastChangedSequence = acknowledgement.changeLines?.find((line) => line.path === path)?.lastChangedSequence;
  // Without a recorded change for the path there is nothing to have acknowledged, so the collision
  // stands. Never guess an acknowledgement from a missing cursor — that would silence a real alert.
  if (lastChangedSequence === undefined) return false;

  return proposalSeenCursorForPath(acknowledgement, path) >= lastChangedSequence;
}

/**
 * Whether a field is in collision. Matches the field's semantic path exactly or as an ancestor of a more
 * granular collision path (e.g. a "/discount/percent" conflict lights up the "/discount" field), keeping
 * the badge field-level like the change history it sits beside.
 */
export function proposalFieldHasCollision(
  collisions: ProposalCollisionKeys,
  field: ProposalField | null | undefined,
): boolean {
  const path = proposalTimelinePathForField(field ?? undefined);
  if (!path) return false;
  for (const collisionPath of collisions.paths) {
    if (collisionPath === path || collisionPath.startsWith(`${path}/`)) return true;
  }
  return false;
}
